import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Zap, 
  Sun, 
  Home,
  ArrowRight,
  ArrowLeft,
  LogOut,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Settings,
  Euro,
  Battery,
  Activity,
  Users,
  CircleDot,
  ArrowRightLeft
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EnergyFlows = () => {
  const navigate = useNavigate();
  const { communityId } = useParams();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState(null);
  const [flowData, setFlowData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [myBalance, setMyBalance] = useState(null);
  const [preferences, setPreferences] = useState(null);
  
  // Form states
  const [offerAmount, setOfferAmount] = useState(10);
  const [offerPrice, setOfferPrice] = useState(0.12);
  const [demandAmount, setDemandAmount] = useState(10);
  const [demandPrice, setDemandPrice] = useState(0.18);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!communityId) return;
    
    try {
      const [communityRes, flowRes, transRes, statsRes, balanceRes, prefsRes] = await Promise.all([
        axios.get(`${API_URL}/api/communities/${communityId}`),
        axios.get(`${API_URL}/api/energy/flow-visualization/${communityId}`),
        axios.get(`${API_URL}/api/energy/transactions/${communityId}?limit=20`),
        axios.get(`${API_URL}/api/energy/community-stats/${communityId}`),
        axios.get(`${API_URL}/api/energy/my-balance`),
        axios.get(`${API_URL}/api/energy/preferences/${communityId}`)
      ]);
      
      setCommunity(communityRes.data);
      setFlowData(flowRes.data);
      setTransactions(transRes.data);
      setStats(statsRes.data);
      setMyBalance(balanceRes.data);
      setPreferences(prefsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        navigate('/communities');
      }
    } finally {
      setLoading(false);
    }
  }, [communityId, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleCreateOffer = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/energy/offer`, {
        community_id: communityId,
        available_kwh: offerAmount,
        min_price_per_kwh: offerPrice
      });
      
      toast.success(`Offre créée : ${offerAmount} kWh à ${offerPrice}€/kWh`);
      
      if (response.data.allocation_result?.transactions_created > 0) {
        toast.success(`${response.data.allocation_result.transactions_created} transaction(s) effectuée(s) !`);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'offre');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateDemand = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/energy/demand`, {
        community_id: communityId,
        requested_kwh: demandAmount,
        max_price_per_kwh: demandPrice
      });
      
      toast.success(`Demande créée : ${demandAmount} kWh à max ${demandPrice}€/kWh`);
      
      if (response.data.allocation_result?.transactions_created > 0) {
        toast.success(`${response.data.allocation_result.transactions_created} transaction(s) effectuée(s) !`);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePreferences = async (newPrefs) => {
    try {
      await axios.put(`${API_URL}/api/energy/preferences`, {
        community_id: communityId,
        ...newPrefs
      });
      setPreferences({ ...preferences, ...newPrefs });
      toast.success('Préférences mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link">Communautés</Link>
              <Link to="/simulator" className="nav-link">Simulateur</Link>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Back Link */}
          <Link 
            to={`/communities/${communityId}`}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la communauté
          </Link>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="page-title">
                Gestion des Flux - {community?.name}
              </h1>
              <p className="text-muted-foreground">
                Échangez de l'énergie avec les membres de votre communauté
              </p>
            </div>
            <Button 
              onClick={fetchData}
              variant="outline"
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </Button>
          </div>

          {/* My Balance Card */}
          {myBalance && (
            <Card className="stats-card mb-8 bg-gradient-to-r from-primary/5 to-accent/5" data-testid="my-balance-card">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Énergie vendue</p>
                    <p className="text-2xl font-bold text-primary">{myBalance.energy_sold_kwh} kWh</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Énergie achetée</p>
                    <p className="text-2xl font-bold text-blue-600">{myBalance.energy_bought_kwh} kWh</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Revenus</p>
                    <p className="text-2xl font-bold text-green-600">+{myBalance.revenue_euros} €</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Dépenses</p>
                    <p className="text-2xl font-bold text-red-500">-{myBalance.expenses_euros} €</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Solde net</p>
                    <p className={`text-2xl font-bold ${myBalance.net_balance_euros >= 0 ? 'text-primary' : 'text-red-500'}`}>
                      {myBalance.net_balance_euros >= 0 ? '+' : ''}{myBalance.net_balance_euros} €
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="exchange" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
              <TabsTrigger value="exchange" data-testid="tab-exchange">
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Échanger
              </TabsTrigger>
              <TabsTrigger value="flows" data-testid="tab-flows">
                <Activity className="w-4 h-4 mr-2" />
                Flux
              </TabsTrigger>
              <TabsTrigger value="transactions" data-testid="tab-transactions">
                <TrendingUp className="w-4 h-4 mr-2" />
                Transactions
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings">
                <Settings className="w-4 h-4 mr-2" />
                Préférences
              </TabsTrigger>
            </TabsList>

            {/* Exchange Tab */}
            <TabsContent value="exchange" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Create Offer */}
                <Card className="stats-card" data-testid="offer-form">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-yellow-600">
                      <Sun className="w-5 h-5" />
                      Vendre de l'énergie
                    </CardTitle>
                    <CardDescription>
                      Proposez votre surplus de production
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <Label className="flex items-center justify-between">
                        <span>Quantité disponible</span>
                        <span className="font-bold text-primary">{offerAmount} kWh</span>
                      </Label>
                      <Slider
                        value={[offerAmount]}
                        onValueChange={(v) => setOfferAmount(v[0])}
                        min={1}
                        max={100}
                        step={1}
                        className="py-4"
                        data-testid="offer-amount-slider"
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <Label className="flex items-center justify-between">
                        <span>Prix minimum</span>
                        <span className="font-bold text-primary">{offerPrice.toFixed(2)} €/kWh</span>
                      </Label>
                      <Slider
                        value={[offerPrice]}
                        onValueChange={(v) => setOfferPrice(v[0])}
                        min={0.05}
                        max={0.30}
                        step={0.01}
                        className="py-4"
                        data-testid="offer-price-slider"
                      />
                    </div>
                    
                    <div className="p-4 bg-yellow-50 rounded-xl">
                      <p className="text-sm text-yellow-800">
                        <strong>Revenu potentiel :</strong> {(offerAmount * offerPrice).toFixed(2)} € minimum
                      </p>
                    </div>
                    
                    <Button 
                      onClick={handleCreateOffer}
                      disabled={submitting}
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
                      data-testid="submit-offer-btn"
                    >
                      {submitting ? <div className="loader w-5 h-5" /> : (
                        <>
                          <Sun className="w-4 h-4 mr-2" />
                          Proposer mon énergie
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Create Demand */}
                <Card className="stats-card" data-testid="demand-form">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-blue-600">
                      <Home className="w-5 h-5" />
                      Acheter de l'énergie
                    </CardTitle>
                    <CardDescription>
                      Exprimez votre besoin en énergie locale
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <Label className="flex items-center justify-between">
                        <span>Quantité souhaitée</span>
                        <span className="font-bold text-primary">{demandAmount} kWh</span>
                      </Label>
                      <Slider
                        value={[demandAmount]}
                        onValueChange={(v) => setDemandAmount(v[0])}
                        min={1}
                        max={100}
                        step={1}
                        className="py-4"
                        data-testid="demand-amount-slider"
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <Label className="flex items-center justify-between">
                        <span>Prix maximum accepté</span>
                        <span className="font-bold text-primary">{demandPrice.toFixed(2)} €/kWh</span>
                      </Label>
                      <Slider
                        value={[demandPrice]}
                        onValueChange={(v) => setDemandPrice(v[0])}
                        min={0.05}
                        max={0.30}
                        step={0.01}
                        className="py-4"
                        data-testid="demand-price-slider"
                      />
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <p className="text-sm text-blue-800">
                        <strong>Coût maximum :</strong> {(demandAmount * demandPrice).toFixed(2)} €
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        vs réseau national : {(demandAmount * 0.25).toFixed(2)} € (économie potentielle: {((demandAmount * 0.25) - (demandAmount * demandPrice)).toFixed(2)} €)
                      </p>
                    </div>
                    
                    <Button 
                      onClick={handleCreateDemand}
                      disabled={submitting}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                      data-testid="submit-demand-btn"
                    >
                      {submitting ? <div className="loader w-5 h-5" /> : (
                        <>
                          <Battery className="w-4 h-4 mr-2" />
                          Demander de l'énergie
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Market Status */}
              {stats && (
                <Card className="stats-card" data-testid="market-status">
                  <CardHeader>
                    <CardTitle>État du marché</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-yellow-50 rounded-xl">
                        <p className="text-2xl font-bold text-yellow-600">{stats.pending_offers || 0}</p>
                        <p className="text-sm text-muted-foreground">Offres en attente</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-xl">
                        <p className="text-2xl font-bold text-blue-600">{stats.pending_demands || 0}</p>
                        <p className="text-sm text-muted-foreground">Demandes en attente</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-xl">
                        <p className="text-2xl font-bold text-green-600">{stats.available_energy_kwh?.toFixed(1) || 0} kWh</p>
                        <p className="text-sm text-muted-foreground">Énergie disponible</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-xl">
                        <p className="text-2xl font-bold text-purple-600">{stats.requested_energy_kwh?.toFixed(1) || 0} kWh</p>
                        <p className="text-sm text-muted-foreground">Énergie demandée</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Flows Tab */}
            <TabsContent value="flows" className="space-y-6">
              {flowData && (
                <>
                  {/* Flow Visualization */}
                  <Card className="stats-card" data-testid="flow-visualization">
                    <CardHeader>
                      <CardTitle>Visualisation des flux</CardTitle>
                      <CardDescription>
                        Membres de la communauté et leurs échanges récents
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {flowData.nodes?.map((node, index) => (
                          <div 
                            key={node.id}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              node.id === user?.id 
                                ? 'border-primary bg-primary/5' 
                                : 'border-muted bg-white'
                            }`}
                            data-testid={`flow-node-${index}`}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                node.type === 'producer' ? 'bg-yellow-100' : 'bg-blue-100'
                              }`}>
                                {node.type === 'producer' ? (
                                  <Sun className="w-5 h-5 text-yellow-600" />
                                ) : (
                                  <Home className="w-5 h-5 text-blue-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-foreground">
                                  {node.name}
                                  {node.id === user?.id && <span className="text-xs text-primary ml-2">(Vous)</span>}
                                </p>
                                <p className="text-xs text-muted-foreground capitalize">
                                  {node.type === 'producer' ? 'Producteur' : 'Consommateur'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="p-2 bg-muted/50 rounded-lg">
                                <p className="text-muted-foreground">Produit</p>
                                <p className="font-semibold text-yellow-600">{node.energy_produced} kWh</p>
                              </div>
                              <div className="p-2 bg-muted/50 rounded-lg">
                                <p className="text-muted-foreground">Consommé</p>
                                <p className="font-semibold text-blue-600">{node.energy_consumed} kWh</p>
                              </div>
                            </div>
                            
                            <div className="flex gap-2 mt-3">
                              {node.has_active_offer && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                                  Offre active
                                </span>
                              )}
                              {node.has_active_demand && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                  Demande active
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Flows */}
                  {flowData.edges?.length > 0 && (
                    <Card className="stats-card" data-testid="recent-flows">
                      <CardHeader>
                        <CardTitle>Flux récents</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {flowData.edges.map((edge, index) => {
                            const fromNode = flowData.nodes?.find(n => n.id === edge.from);
                            const toNode = flowData.nodes?.find(n => n.id === edge.to);
                            
                            return (
                              <div 
                                key={index}
                                className="flex items-center gap-4 p-3 bg-muted/50 rounded-xl"
                                data-testid={`flow-edge-${index}`}
                              >
                                <div className="flex items-center gap-2">
                                  <Sun className="w-4 h-4 text-yellow-500" />
                                  <span className="font-medium">{fromNode?.name || 'Producteur'}</span>
                                </div>
                                <div className="flex-1 flex items-center justify-center">
                                  <div className="flex items-center gap-2 px-4 py-1 bg-primary/10 rounded-full">
                                    <span className="font-bold text-primary">{edge.energy_kwh} kWh</span>
                                    <ArrowRight className="w-4 h-4 text-primary" />
                                    <span className="text-sm text-muted-foreground">{edge.price_per_kwh?.toFixed(2)}€/kWh</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{toNode?.name || 'Consommateur'}</span>
                                  <Home className="w-4 h-4 text-blue-500" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="space-y-6">
              <Card className="stats-card" data-testid="transactions-list">
                <CardHeader>
                  <CardTitle>Historique des transactions</CardTitle>
                  <CardDescription>
                    Toutes les transactions de la communauté
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {transactions.length === 0 ? (
                    <div className="text-center py-8">
                      <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Aucune transaction pour le moment</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Créez une offre ou une demande pour commencer
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.map((transaction, index) => (
                        <div 
                          key={transaction.id}
                          className={`p-4 rounded-xl border ${
                            transaction.producer_id === user?.id || transaction.consumer_id === user?.id
                              ? 'border-primary/30 bg-primary/5'
                              : 'border-muted bg-white'
                          }`}
                          data-testid={`transaction-${index}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{transaction.producer_name}</span>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{transaction.consumer_name}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(transaction.timestamp).toLocaleString('fr-FR')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <span className="text-lg font-bold text-primary">{transaction.energy_kwh} kWh</span>
                              <span className="text-sm text-muted-foreground">
                                @ {transaction.price_per_kwh?.toFixed(2)} €/kWh
                              </span>
                            </div>
                            <span className="text-lg font-bold text-foreground">
                              {transaction.total_amount?.toFixed(2)} €
                            </span>
                          </div>
                          {(transaction.producer_id === user?.id || transaction.consumer_id === user?.id) && (
                            <div className="mt-2 pt-2 border-t border-muted">
                              <span className={`text-sm font-medium ${
                                transaction.producer_id === user?.id ? 'text-green-600' : 'text-blue-600'
                              }`}>
                                {transaction.producer_id === user?.id ? '↑ Vous avez vendu' : '↓ Vous avez acheté'}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Community Stats */}
              {stats && stats.total_transactions > 0 && (
                <Card className="stats-card" data-testid="community-stats">
                  <CardHeader>
                    <CardTitle>Statistiques de la communauté</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-muted/50 rounded-xl">
                        <p className="text-2xl font-bold text-foreground">{stats.total_transactions}</p>
                        <p className="text-sm text-muted-foreground">Transactions</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-xl">
                        <p className="text-2xl font-bold text-primary">{stats.total_energy_exchanged_kwh?.toFixed(1)} kWh</p>
                        <p className="text-sm text-muted-foreground">Énergie échangée</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-xl">
                        <p className="text-2xl font-bold text-foreground">{stats.total_value_euros?.toFixed(2)} €</p>
                        <p className="text-sm text-muted-foreground">Valeur totale</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-xl">
                        <p className="text-2xl font-bold text-foreground">{stats.average_price_per_kwh?.toFixed(3)} €</p>
                        <p className="text-sm text-muted-foreground">Prix moyen/kWh</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6">
              {preferences && (
                <Card className="stats-card" data-testid="preferences-form">
                  <CardHeader>
                    <CardTitle>Mes préférences</CardTitle>
                    <CardDescription>
                      Configurez vos paramètres d'échange automatique
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <Label className="flex items-center justify-between">
                          <span>Prix max d'achat</span>
                          <span className="font-bold text-blue-600">{preferences.max_price_per_kwh?.toFixed(2)} €/kWh</span>
                        </Label>
                        <Slider
                          value={[preferences.max_price_per_kwh || 0.20]}
                          onValueChange={(v) => handleUpdatePreferences({ max_price_per_kwh: v[0] })}
                          min={0.05}
                          max={0.30}
                          step={0.01}
                          className="py-4"
                          data-testid="pref-max-price-slider"
                        />
                        <p className="text-xs text-muted-foreground">
                          Prix maximum que vous acceptez de payer pour l'énergie locale
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        <Label className="flex items-center justify-between">
                          <span>Prix min de vente</span>
                          <span className="font-bold text-yellow-600">{preferences.min_price_per_kwh?.toFixed(2)} €/kWh</span>
                        </Label>
                        <Slider
                          value={[preferences.min_price_per_kwh || 0.10]}
                          onValueChange={(v) => handleUpdatePreferences({ min_price_per_kwh: v[0] })}
                          min={0.05}
                          max={0.30}
                          step={0.01}
                          className="py-4"
                          data-testid="pref-min-price-slider"
                        />
                        <p className="text-xs text-muted-foreground">
                          Prix minimum auquel vous acceptez de vendre votre surplus
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <Label className="flex items-center justify-between">
                        <span>Priorité</span>
                        <span className="font-bold text-primary">{preferences.priority || 5}/10</span>
                      </Label>
                      <Slider
                        value={[preferences.priority || 5]}
                        onValueChange={(v) => handleUpdatePreferences({ priority: v[0] })}
                        min={1}
                        max={10}
                        step={1}
                        className="py-4"
                        data-testid="pref-priority-slider"
                      />
                      <p className="text-xs text-muted-foreground">
                        1 = Haute priorité (servi en premier), 10 = Basse priorité
                      </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6 pt-4 border-t">
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                        <div>
                          <p className="font-medium">Vente automatique</p>
                          <p className="text-sm text-muted-foreground">Vendre automatiquement le surplus</p>
                        </div>
                        <Switch
                          checked={preferences.auto_sell}
                          onCheckedChange={(v) => handleUpdatePreferences({ auto_sell: v })}
                          data-testid="pref-auto-sell-switch"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                        <div>
                          <p className="font-medium">Achat automatique</p>
                          <p className="text-sm text-muted-foreground">Acheter automatiquement si disponible</p>
                        </div>
                        <Switch
                          checked={preferences.auto_buy}
                          onCheckedChange={(v) => handleUpdatePreferences({ auto_buy: v })}
                          data-testid="pref-auto-buy-switch"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default EnergyFlows;
